import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Mail,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Send,
  Sparkles,
  KeyRound,
  ShieldCheck,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/password-reset-request`, { email });
      setSuccess(true);
      if (response.data.token) setResetToken(response.data.token);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) setError('Aucun compte trouvé avec cet email.');
      else if (status === 429) setError('Trop de tentatives. Veuillez réessayer dans quelques minutes.');
      else if (!err.response) setError('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
      else setError(detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      variant="default"
      badge="Récupération de compte"
      badgeIcon={KeyRound}
      title="Mot de passe"
      titleHighlight="oublié ?"
      description="Pas de panique. Saisissez votre email professionnel pour recevoir un lien sécurisé de réinitialisation."
      features={[
        { icon: ShieldCheck, label: 'Lien sécurisé chiffré (TLS 1.3)' },
        { icon: CheckCircle, label: 'Validité 30 minutes' },
        { icon: Sparkles, label: 'Aucune information ne quitte la plateforme' },
      ]}
    >
      <div className="mb-2">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          data-testid="forgot-back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </Link>
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mt-3 mb-2">
        Réinitialiser votre mot de passe
      </h2>
      <p className="text-slate-600 dark:text-slate-300 text-sm mb-7">
        Nous vous enverrons un lien sécurisé pour définir un nouveau mot de passe.
      </p>

      {success ? (
        <div className="space-y-5" data-testid="forgot-success-state">
          <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div>
              <p className="font-semibold text-emerald-800 dark:text-emerald-200">Email envoyé !</p>
              <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
                Si un compte est associé à cet email, vous recevrez un lien de réinitialisation dans quelques instants.
              </p>
            </div>
          </div>

          {resetToken && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <Sparkles className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-blue-800 dark:text-blue-200 mb-1.5 text-sm">Mode développement — Token :</p>
                <code className="block text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200 px-3 py-2 rounded-lg break-all">
                  {resetToken}
                </code>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Link to="/reset-password" className="flex-1">
              <Button className="w-full h-12 bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] text-white font-semibold shadow-lg shadow-blue-900/20">
                Réinitialiser
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login" className="flex-1">
              <Button variant="outline" className="w-full h-12 font-semibold">
                Connexion
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span data-testid="forgot-error-message">{error}</span>
            </div>
          )}

          <div>
            <Label htmlFor="forgot-email" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Adresse email
            </Label>
            <div className="relative mt-1.5">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                className="pl-10 h-12"
                required
                autoComplete="email"
                data-testid="forgot-email-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] shadow-lg shadow-blue-900/30 hover:shadow-xl group text-white"
            data-testid="forgot-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Envoi en cours…
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Envoyer le lien
              </>
            )}
          </Button>
        </form>
      )}

      <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Pas encore de compte ?{' '}
          <Link to="/register" className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">
            Créer un compte
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
